import { Component, Input, OnInit } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";

export interface AlternativeOption {
  id: number;
  sku: string;
  reserve: number;
  limit: number;
  reserveFormula?: string;
  limitFormula?: string;
  isOriginal: boolean;
  order?: number;
}

export interface AlternativeSelectionData {
  demandItemId: number;
  demandItemName: string;
  originalSku: string;
  originalReserve: number;
  originalLimit: number;
  originalReserveFormula?: string;
  originalLimitFormula?: string;
  alternatives: any[];
  /** Order of the previously chosen alternative (0 = original, undefined = none). */
  currentOrder?: number;
}

/** Sentinel returned when the user clears their previous selection. */
export const ALTERNATIVE_SELECTION_CLEARED = "__cleared__" as const;

@Component({
  standalone: false,
  templateUrl: "./alternative-selection-modal.component.html",
  styleUrls: ["./alternative-selection-modal.component.scss"],
})
export class AlternativeSelectionModalComponent implements OnInit {
  @Input() data!: AlternativeSelectionData;

  options: AlternativeOption[] = [];
  selectedOption: AlternativeOption | null = null;

  constructor(public activeModal: NgbActiveModal) {}

  ngOnInit(): void {
    // Add original item as first option
    this.options.push({
      id: 0,
      sku: this.data.originalSku,
      reserve: this.data.originalReserve,
      limit: this.data.originalLimit,
      reserveFormula: this.data.originalReserveFormula,
      limitFormula: this.data.originalLimitFormula,
      isOriginal: true,
    });

    // Add alternatives
    for (const alt of this.data.alternatives) {
      this.options.push({
        id: alt.Id,
        sku: alt.SKU,
        reserve: alt.ReserveCalculated,
        limit: alt.LimitCalculated,
        reserveFormula: alt.ReserveFormula,
        limitFormula: alt.LimitFormula,
        isOriginal: false,
        order: alt.Order,
      });
    }

    // Pre-select the previously chosen option if any, otherwise the original.
    if (this.data.currentOrder !== undefined && this.data.currentOrder !== null) {
      const previous = this.options.find((o) =>
        this.data.currentOrder === 0 ? o.isOriginal : o.order === this.data.currentOrder
      );
      this.selectedOption = previous ?? this.options[0];
    } else {
      this.selectedOption = this.options[0];
    }
  }

  selectOption(option: AlternativeOption): void {
    this.selectedOption = option;
  }

  confirm(): void {
    this.activeModal.close(this.selectedOption);
  }

  /** Clears any previous selection and signals the parent to drop it. */
  clearSelection(): void {
    this.activeModal.close(ALTERNATIVE_SELECTION_CLEARED);
  }

  cancel(): void {
    this.activeModal.dismiss("cancel");
  }
}
